import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Megaphone, Send, Loader2 } from "lucide-react";

export default function BroadcastsTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", channel: "inapp", action_url: "" });
  const [sending, setSending] = useState(false);

  const load = () => apiClient.get("/admin/broadcasts").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return;
    setSending(true);
    try {
      await apiClient.post("/admin/broadcasts", form);
      setForm({ title: "", body: "", audience: "all", channel: "inapp", action_url: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-broadcasts" className="space-y-6">
      <h2 className="font-display text-2xl flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "الإشعارات الجماعية" : "Broadcasts"}</h2>

      <form onSubmit={send} className="surface rounded-xl p-6 space-y-4">
        <div>
          <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "العنوان" : "Title"}</label>
          <input className="input-dark w-full" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="broadcast-title" />
        </div>
        <div>
          <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "نص الرسالة" : "Body"}</label>
          <textarea className="input-dark w-full min-h-[120px]" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="broadcast-body" />
        </div>
        <div>
          <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "الوجهة عند الضغط" : "Destination on tap"}</label>
          <select
            className="input-dark w-full"
            value={["", "/verify", "/dashboard"].includes(form.action_url) ? form.action_url : "custom"}
            onChange={(e) => setForm({ ...form, action_url: e.target.value === "custom" ? "" : e.target.value })}
            data-testid="broadcast-action-url-preset"
          >
            <option value="">{lang === "ar" ? "قائمة الإشعارات (تلقائي)" : "Notifications list (default)"}</option>
            <option value="/verify">{lang === "ar" ? "التحقق من الحساب" : "Account Verification"}</option>
            <option value="/dashboard">{lang === "ar" ? "لوحة التحكم" : "Dashboard"}</option>
            <option value="custom">{lang === "ar" ? "مخصص..." : "Custom…"}</option>
          </select>
          {!["", "/verify", "/dashboard"].includes(form.action_url) && (
            <input
              className="input-dark w-full mt-2"
              placeholder="/custom-path"
              value={form.action_url}
              onChange={(e) => setForm({ ...form, action_url: e.target.value })}
              data-testid="broadcast-action-url-custom"
            />
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "الجمهور" : "Audience"}</label>
            <select className="input-dark w-full" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="broadcast-audience">
              <option value="all">{lang === "ar" ? "الكل" : "All"}</option>
              <option value="dealers">{lang === "ar" ? "التجار/الوكالات" : "Dealers/Agencies"}</option>
              <option value="individuals">{lang === "ar" ? "الأفراد" : "Individuals"}</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "القناة" : "Channel"}</label>
            <select className="input-dark w-full" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="broadcast-channel">
              <option value="inapp">{lang === "ar" ? "داخل التطبيق" : "In-app"}</option>
              <option value="email">{lang === "ar" ? "بريد إلكتروني" : "Email"}</option>
              <option value="sms">SMS</option>
            </select>
          </div>
        </div>
        <button type="submit" disabled={sending} className="btn-primary" data-testid="broadcast-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {lang === "ar" ? "إرسال للجميع" : "Send"}
        </button>
      </form>

      <TargetedSendCard lang={lang} />

      <div>
        <h3 className="font-display text-lg mb-3">{lang === "ar" ? "آخر الإشعارات المرسلة" : "Recent broadcasts"} ({items.length})</h3>
        <div className="space-y-3">
          {items.length === 0 ? <div className="surface rounded-xl p-10 text-center text-[#6b6b6b]">{lang === "ar" ? "لا إشعارات مُرسلة بعد." : "No broadcasts yet."}</div> : items.map(b => (
            <div key={b.id} className="surface rounded-xl p-5" data-testid={`broadcast-${b.id}`}>
              <div className="flex justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="font-display text-base">{b.title}</div>
                  <p className="text-sm text-[#3a3a3a] mt-1">{b.body}</p>
                  <div className="text-xs text-[#8a8a8a] mt-2">{b.audience} · {b.channel} · {b.recipients} {lang === "ar" ? "مستلم" : "recipients"} · {new Date(b.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TargetedSendCard({ lang }) {
  const [form, setForm] = useState({ user_id: "", title: "", body: "", kind: "admin.alert", email: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.title || !form.body) return;
    setSending(true); setResult(null);
    try {
      await apiClient.post("/admin/notifications/send", form);
      setResult({ ok: true });
      setForm({ user_id: "", title: "", body: "", kind: "admin.alert", email: false });
    } catch (err) {
      setResult({ ok: false, msg: err.response?.data?.detail || "Error" });
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} className="surface rounded-xl p-6 space-y-3" data-testid="admin-targeted-send">
      <h3 className="font-display text-lg flex items-center gap-2"><Send className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "إرسال لمستخدم محدد" : "Send to a specific user"}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "معرّف المستخدم (user_id)" : "User ID"}</label>
          <input className="input-dark w-full" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required data-testid="targeted-uid" />
        </div>
        <div>
          <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "نوع الإشعار" : "Kind"}</label>
          <select className="input-dark w-full" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="targeted-kind">
            <option value="admin.alert">{lang === "ar" ? "تنبيه إداري" : "Admin alert"}</option>
            <option value="admin.broadcast">{lang === "ar" ? "إعلان عام" : "Broadcast"}</option>
          </select>
        </div>
      </div>
      <input className="input-dark w-full" value={form.title} placeholder={lang === "ar" ? "العنوان" : "Title"} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="targeted-title" />
      <textarea className="input-dark w-full min-h-[90px]" value={form.body} placeholder={lang === "ar" ? "النص" : "Body"} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="targeted-body" />
      <label className="flex items-center gap-2 text-xs text-[#525252] cursor-pointer">
        <input type="checkbox" checked={form.email} onChange={(e) => setForm({ ...form, email: e.target.checked })} data-testid="targeted-email" />
        {lang === "ar" ? "إرسال نسخة بالبريد أيضاً" : "Also send by email"}
      </label>
      <button type="submit" disabled={sending} className="btn-primary text-sm" data-testid="targeted-send">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {lang === "ar" ? "إرسال" : "Send"}
      </button>
      {result && (
        <p className={`text-xs ${result.ok ? "text-[#0a4f3a]" : "text-red-700"}`}>
          {result.ok ? (lang === "ar" ? "تم الإرسال" : "Sent") : result.msg}
        </p>
      )}
    </form>
  );
}
